import {
    useCancelBillingTransferRequestMutation,
    useRequestBillingTransferFromOrganizationMutation,
} from "@api/billingTransfer.api";
import { Organization } from "@common/types/organization";
import { Workspace } from "@common/types/workspace";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { FC, useCallback } from "react";

interface BillingTransferRequestsProps {
    organization: Organization;
    workspace: Workspace;
    pendingRequestId?: string;
}

export const BillingTransferRequests: FC<BillingTransferRequestsProps> = ({
    organization,
    workspace,
    pendingRequestId,
}) => {
    const { toast } = useToaster();
    const requestBillingOwnershipMutation =
        useRequestBillingTransferFromOrganizationMutation();
    const cancelRequestMutation = useCancelBillingTransferRequestMutation();

    const handleCancel = useCallback(
        async (requestId: string) => {
            await cancelRequestMutation.mutateAsync(requestId);
            toast("Transfer request canceled");
        },
        [cancelRequestMutation, toast],
    );

    const handleRequest = useCallback(async () => {
        await requestBillingOwnershipMutation.mutateAsync({
            fromOrganizationId: organization.id,
            workspaceId: workspace.id,
        });
        toast("Billing ownership request sent");
    }, [organization.id, requestBillingOwnershipMutation, toast, workspace.id]);

    if (!pendingRequestId) {
        return (
            <ButtonWithLoader
                variant="primary"
                size="md"
                loading={requestBillingOwnershipMutation.isPending}
                onClick={handleRequest}
            >
                Request billing ownership
            </ButtonWithLoader>
        );
    }

    return (
        <>
            <span>Billing ownership requested</span>
            <ButtonWithLoader
                variant="primary"
                size="md"
                loading={cancelRequestMutation.isPending}
                onClick={() => handleCancel(pendingRequestId)}
            >
                Cancel request
            </ButtonWithLoader>
        </>
    );
};
